import { useState, useEffect } from 'react';
import { useUpdateOAuthConfig, useOAuthRedirectUri } from '../../hooks/useOAuthConfigs';
import type { OAuthConfig, UpdateOAuthConfigRequest } from '../../types/oauth';
import { PROVIDER_DISPLAY_NAMES, DEFAULT_SCOPES } from '../../types/oauth';
import './OAuthConfigEditForm.css';

interface OAuthConfigEditFormProps {
  projectId: string;
  config: OAuthConfig;
  onClose: () => void;
  onSuccess: () => void;
}

export function OAuthConfigEditForm({ projectId, config, onClose, onSuccess }: OAuthConfigEditFormProps) {
  const [clientId, setClientId] = useState(config.client_id);
  const [clientSecret, setClientSecret] = useState('');
  const [scopes, setScopes] = useState<string[]>(config.scopes);
  const [customScope, setCustomScope] = useState('');
  const [providerSettings, setProviderSettings] = useState<Record<string, string>>(
    config.provider_settings as Record<string, string>
  );
  const [copied, setCopied] = useState(false);
  const [showSecretWarning, setShowSecretWarning] = useState(false);

  const { data: redirectUriData } = useOAuthRedirectUri(projectId, config.provider);
  const updateConfig = useUpdateOAuthConfig();

  const redirectUri = redirectUriData?.redirect_uri || '';

  useEffect(() => {
    // Show warning when changing credentials on a tested config
    if (clientSecret && config.tested_at) {
      setShowSecretWarning(true);
    } else {
      setShowSecretWarning(false);
    }
  }, [clientSecret, config.tested_at]);

  const handleCopyRedirectUri = async () => {
    await navigator.clipboard.writeText(redirectUri);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAddScope = () => {
    const trimmed = customScope.trim();
    if (trimmed && !scopes.includes(trimmed)) {
      setScopes([...scopes, trimmed]);
      setCustomScope('');
    }
  };

  const handleRemoveScope = (scope: string) => {
    setScopes(scopes.filter((s) => s !== scope));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data: UpdateOAuthConfigRequest = {};

    // Only include changed fields
    if (clientId !== config.client_id) {
      data.client_id = clientId.trim();
    }
    if (clientSecret) {
      data.client_secret = clientSecret;
    }
    if (JSON.stringify(scopes) !== JSON.stringify(config.scopes)) {
      data.scopes = scopes;
    }

    const cleanSettings = Object.fromEntries(
      Object.entries(providerSettings).filter(([, v]) => v !== '')
    );
    if (JSON.stringify(cleanSettings) !== JSON.stringify(config.provider_settings)) {
      data.provider_settings = cleanSettings;
    }

    // Don't submit if nothing changed
    if (Object.keys(data).length === 0) {
      onClose();
      return;
    }

    try {
      const result = await updateConfig.mutateAsync({
        projectId,
        provider: config.provider,
        data,
      });

      if (result.warning) {
        // Could show a toast notification here
        console.log('Warning:', result.warning);
      }

      onSuccess();
    } catch {
      // Error handled by mutation
    }
  };

  const renderProviderSettings = () => {
    switch (config.provider) {
      case 'google':
        return (
          <div className="form-group">
            <label htmlFor="hostedDomain">Hosted Domain (Optional)</label>
            <input
              id="hostedDomain"
              type="text"
              value={providerSettings.hosted_domain || ''}
              onChange={(e) => setProviderSettings({ ...providerSettings, hosted_domain: e.target.value })}
              placeholder="example.com"
            />
            <span className="form-hint">
              Restrict sign-in to users from a specific Google Workspace domain.
            </span>
          </div>
        );

      case 'microsoft':
        return (
          <div className="form-group">
            <label htmlFor="tenantId">Tenant ID</label>
            <select
              id="tenantId"
              value={providerSettings.tenant_id || 'common'}
              onChange={(e) => setProviderSettings({ ...providerSettings, tenant_id: e.target.value })}
            >
              <option value="common">Common (Any Microsoft account)</option>
              <option value="organizations">Organizations only (Work/School accounts)</option>
              <option value="consumers">Consumers only (Personal accounts)</option>
            </select>
            <span className="form-hint">
              Controls which Microsoft accounts can sign in to your application.
            </span>
          </div>
        );

      case 'okta':
        return (
          <>
            <div className="form-group">
              <label htmlFor="issuer">Okta Issuer URL *</label>
              <input
                id="issuer"
                type="url"
                value={providerSettings.issuer || ''}
                onChange={(e) => setProviderSettings({ ...providerSettings, issuer: e.target.value })}
                placeholder="https://dev-123456.okta.com"
                required
              />
              <span className="form-hint">
                Your Okta organization URL.
              </span>
            </div>
            <div className="form-group">
              <label htmlFor="authServer">Authorization Server (Optional)</label>
              <input
                id="authServer"
                type="text"
                value={providerSettings.authorization_server || ''}
                onChange={(e) => setProviderSettings({ ...providerSettings, authorization_server: e.target.value })}
                placeholder="default"
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--edit-form" onClick={(e) => e.stopPropagation()}>
        <header className="modal__header">
          <h2>Edit {PROVIDER_DISPLAY_NAMES[config.provider]} Configuration</h2>
          <button className="modal__close" onClick={onClose} type="button">
            &times;
          </button>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="modal__body">
            {/* Status Info */}
            <div className="edit-form__status">
              <div className="status-item">
                <span className="status-label">Status</span>
                <span className={`status-badge status-badge--${config.enabled ? 'enabled' : 'disabled'}`}>
                  {config.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Last Tested</span>
                <span className={config.tested_at ? 'text-success' : 'text-warning'}>
                  {config.tested_at
                    ? new Date(config.tested_at).toLocaleString()
                    : 'Not tested'
                  }
                </span>
              </div>
            </div>

            {/* Redirect URI */}
            <div className="form-section">
              <h4>Redirect URI</h4>
              <div className="redirect-uri-box">
                <code>{redirectUri || 'Loading...'}</code>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={handleCopyRedirectUri}
                  disabled={!redirectUri}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Credentials */}
            <div className="form-section">
              <h4>Credentials</h4>

              {showSecretWarning && (
                <div className="form-warning">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                  <span>
                    Changing the client secret will reset the tested status. You'll need to re-test the connection before enabling.
                  </span>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="clientId">Client ID</label>
                <input
                  id="clientId"
                  type="text"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="clientSecret">Client Secret</label>
                <input
                  id="clientSecret"
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder="Leave empty to keep current secret"
                  autoComplete="new-password"
                />
                <span className="form-hint">
                  Current secret: {config.client_secret_masked}
                </span>
              </div>
            </div>

            {/* Scopes */}
            <div className="form-section">
              <h4>Scopes</h4>
              <div className="scopes-list">
                {scopes.map((scope) => (
                  <div key={scope} className="scope-tag">
                    <span>{scope}</span>
                    <button
                      type="button"
                      className="scope-tag__remove"
                      onClick={() => handleRemoveScope(scope)}
                      aria-label={`Remove ${scope}`}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
              <div className="add-scope">
                <input
                  type="text"
                  value={customScope}
                  onChange={(e) => setCustomScope(e.target.value)}
                  placeholder="Add custom scope"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddScope();
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={handleAddScope}
                  disabled={!customScope.trim()}
                >
                  Add
                </button>
              </div>
              <button
                type="button"
                className="btn btn--link btn--sm"
                onClick={() => setScopes(DEFAULT_SCOPES[config.provider] || [])}
              >
                Reset to defaults
              </button>
            </div>

            {/* Provider Settings */}
            {renderProviderSettings() && (
              <div className="form-section">
                <h4>Provider Settings</h4>
                {renderProviderSettings()}
              </div>
            )}

            {/* Error Display */}
            {updateConfig.isError && (
              <div className="form-error-box">
                <p>{updateConfig.error?.message || 'Failed to update configuration'}</p>
              </div>
            )}
          </div>

          <footer className="modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
              disabled={updateConfig.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={updateConfig.isPending || !clientId.trim()}
            >
              {updateConfig.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
